"use client";
import { IResource } from "@gettactic/api";
import { useAreas } from "@gettactic/hooks";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  ResourceSettingsActionType,
  useResourceSettingsState
} from "@/features/reserve/hooks/useResourceSettings";
import { api } from "@/lib/api/api";
import { useRouter } from "@/lib/useRouter";

export function useEditResourceRouter(
  officeId: string,
  entities: IResource["type"][]
) {
  const [query, setQuery] = useState<string>("");
  const [bulk, setBulk] = useState<string[]>([]);
  const [floor, setFloor] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<IResource[]>([]);
  const [isSideDrawerOpen, setIsSideDrawerOpen] = useState<boolean>(false);
  const [pendingResourceId, setPendingResourceId] = useState<string | null>(
    null
  );
  const resourceContext = useResourceSettingsState();
  const router = useRouter();
  const { areas } = useAreas(officeId);
  const resourceListQuery = useQuery({
    queryKey: [
      "organizations.resourcesAll",
      officeId,
      entities,
      floor,
      null,
      undefined,
      undefined,
      query,
      undefined,
      undefined,
      !!floor
    ],

    queryFn: async () => {
      const res = await api.client.organizations.getAllResources({
        officeId: officeId,
        types: entities,
        parentId: floor ?? undefined,
        search: query ?? undefined
      });
      return res.result;
    },

    enabled: entities.length > 0 && !!officeId
  });
  const openResourceId = useCallback(
    (resId: string) => {
      // The list loads only once a resource is picked, so on a large office an
      // edit asked for right away would otherwise be dropped: open it once the
      // list arrives instead.
      if (!resourceListQuery.data) {
        if (resourceListQuery.isError) {
          resourceListQuery.refetch();
        }
        setPendingResourceId(resId);
        return;
      }
      const resource = resourceListQuery.data.byId[resId];
      if (resource) {
        const { edit, ...rest } = router.query;
        // replace the edit param, so it's not opened multiple times
        if (router.pathname !== "/map") {
          router.replace({
            query: { ...rest },
            hash: router.asPath.split("#")[1]
          });
        }
        resourceContext.dispatch({
          type: ResourceSettingsActionType.SetSelectedResource,
          payload: resource
        });
        setIsSideDrawerOpen(true);
      }
    },
    [
      resourceListQuery.data,
      resourceListQuery.isError,
      resourceListQuery.refetch,
      router,
      resourceContext
    ]
  );
  useEffect(() => {
    if (!pendingResourceId || !resourceListQuery.data) {
      return;
    }
    setPendingResourceId(null);
    openResourceId(pendingResourceId);
  }, [pendingResourceId, resourceListQuery.data, openResourceId]);
  useEffect(() => {
    // Without the list there is nothing to open: tell the user instead of
    // leaving the click without any feedback.
    if (
      !pendingResourceId ||
      !resourceListQuery.isError ||
      resourceListQuery.isFetching
    ) {
      return;
    }
    setPendingResourceId(null);
    toast.error("Sorry, we couldn't load this resource. Please try again.");
  }, [
    pendingResourceId,
    resourceListQuery.isError,
    resourceListQuery.isFetching
  ]);
  useEffect(() => {
    if (
      !router.isReady ||
      !resourceListQuery.data ||
      !router.query?.edit?.toString()
    ) {
      return;
    }
    openResourceId(router.query?.edit?.toString());
  }, [router.isReady, router.query, resourceListQuery.data, openResourceId]);

  const resourceList = resourceListQuery.data?.elements ?? [];
  const selectedAll = resourceList.length
    ? resourceList.length === bulk.length
    : false;
  const visibleSelected = resourceList.filter((x) => bulk.indexOf(x.id) > -1);

  return {
    resourceContext,
    resourceList,
    deleteModal,
    setDeleteModal,
    isSideDrawerOpen,
    setIsSideDrawerOpen,
    areas,
    openResourceId,
    query,
    setQuery,
    floor,
    setFloor,
    bulk,
    setBulk,
    selectedAll,
    visibleSelected
  };
}
